/* eslint-disable max-len */
/* eslint-disable react/prop-types */
/* eslint-disable react/forbid-prop-types */
/* eslint-disable object-curly-newline */
/* eslint-disable import/no-extraneous-dependencies */
import React, {
  useContext, useEffect, useRef, useState,
} from 'react';
import { Tree, TreeNode } from 'react-organizational-chart';
import _ from 'lodash';
import moment from 'moment-timezone';
import { TransformWrapper, TransformComponent } from '@kokarn/react-zoom-pan-pinch';
import { Link } from 'react-router-dom';
import {
  Plus, Minus, Maximize2, X, Bell, Activity, Users, ChevronRight, ChevronDown,
} from 'lucide-react';
import API from '../../api/endpoints';
import { UserContext } from '../../contexts/User';
import './organizationChart.css';
import { convertSecToTimeFormat, currentMonthWeekRange, getDatesInRange } from '../../utils/time';
import DateRangePicker from '../../components/dateRangePicker';
import { Navbar, Sidebar } from '../../components';
import SubordinatesModal from './Subordinates';
import { sendRequest } from '../../api/shootAPI';
import alertImg from '../../images/alert.svg';
import noticeImg from '../../images/notification.svg';
import { STYLED_SELECT_CLASS, STYLED_SELECT_STYLE } from '../../utils/styledSelect';

const AVATAR_PALETTE = [
  { bg: '#fdebe6', fg: '#b94f3c' },
  { bg: '#fde6d2', fg: '#F19235' },
  { bg: '#eef5ea', fg: '#53A551' },
  { bg: '#e8eef7', fg: '#4062E5' },
  { bg: '#f5e6f7', fg: '#9333ea' },
];

function avatarColors(key) {
  const str = String(key || '');
  let h = 0;
  // eslint-disable-next-line no-bitwise
  for (let i = 0; i < str.length; i += 1) h = (h * 31 + str.charCodeAt(i)) >>> 0;
  return AVATAR_PALETTE[h % AVATAR_PALETTE.length];
}

function initialsFromName(name, fallbackKey) {
  const parts = `${name || ''}`.trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
  if (parts.length === 1 && parts[0] !== 'Unknown') return parts[0].slice(0, 2).toUpperCase();
  return `${fallbackKey || '?'}`.slice(0, 2).toUpperCase();
}

// Engagement score color — matches Team Engagement (reports) thresholds.
function engagementColor(pct) {
  if (pct >= 70) return '#53A551';
  if (pct >= 40) return '#F19235';
  return '#b94f3c';
}

// Engagement score = active / total tracked time (same formula as the
// Team Engagement > individual member view: round(activeSeconds*100/totalSeconds)).
function engagementScore(activeSeconds, totalSeconds) {
  return totalSeconds > 0 ? Math.round((activeSeconds * 100) / totalSeconds) : 0;
}

function NodeAvatar({ src, name, id, size = 40 }) {
  const [error, setError] = useState(false);
  const c = avatarColors(id || name);
  const showImg = src && !error;
  return (
    <div
      className="rounded-full flex items-center justify-center flex-shrink-0 overflow-hidden"
      style={{ width: size, height: size, backgroundColor: c.bg, color: c.fg, fontSize: size * 0.32, fontWeight: 700 }}
    >
      {showImg ? (
        <img src={src} alt="" className="w-full h-full object-cover" onError={() => setError(true)} />
      ) : initialsFromName(name, id)}
    </div>
  );
}

// ---- Node card --------------------------------------------------------------
function MemberNode({ org, selected, onShowDetails, onShowSubordinates }) {
  const worked = org.hoursWorked || 0;
  const idle = org.idleTime || 0;
  const engagement = engagementScore(org.activeSeconds || 0, org.totalSeconds || 0);
  const engColor = engagementColor(engagement);
  const reports = org.subordinates?.length || 0;

  return (
    <div
      className={`org-node ${selected ? 'is-selected' : ''}`}
      role="button"
      tabIndex={0}
      onClick={() => onShowDetails(org)}
      onKeyDown={(e) => { if (e.key === 'Enter') onShowDetails(org); }}
    >
      <div className="flex items-center gap-2.5">
        <button
          type="button"
          aria-label={reports ? `View ${reports} team member${reports === 1 ? '' : 's'}` : 'Member avatar'}
          title={reports ? `View ${reports} team member${reports === 1 ? '' : 's'}` : undefined}
          onMouseDown={(e) => e.stopPropagation()}
          onClick={(e) => { e.stopPropagation(); if (reports) onShowSubordinates(org); }}
          className="bg-transparent border-0 p-0 m-0"
          style={{ cursor: reports ? 'pointer' : 'default', lineHeight: 0 }}
        >
          <NodeAvatar src={org.profilePicture} name={org.name} id={org.id} size={40} />
        </button>
        <div className="min-w-0 flex-1">
          <div className="org-node-name">{org.name}</div>
          <div className="org-node-role">{org.role}</div>
        </div>
      </div>

      {org.department ? <span className="org-chip">{org.department}</span> : null}

      <hr className="org-divider" />

      <div className="org-util-row">
        <span className="org-util-label">Engagement Score</span>
        <span className="org-util-pct" style={{ color: engColor }}>{`${engagement}%`}</span>
      </div>
      <div className="org-util-track">
        <div className="org-util-fill" style={{ width: `${engagement}%`, background: engColor }} />
      </div>

      <div className="org-stats">
        <div className="org-stat">
          <span className="org-stat-label">Worked</span>
          <span className="org-stat-value" style={{ color: '#53A551' }}>{convertSecToTimeFormat(worked, 'HH:MM')}</span>
        </div>
        <div className="org-stat">
          <span className="org-stat-label">Idle</span>
          <span className="org-stat-value" style={{ color: '#b9791f' }}>{convertSecToTimeFormat(idle, 'HH:MM')}</span>
        </div>
      </div>
    </div>
  );
}

function LeafToggle({ count, expanded, onClick }) {
  return (
    <button
      type="button"
      className="leaf-toggle"
      onMouseDown={(e) => e.stopPropagation()}
      onTouchStart={(e) => e.stopPropagation()}
      onClick={(e) => { e.stopPropagation(); onClick(); }}
    >
      <span className="leaf-toggle-caret">{expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}</span>
      {`${count} ${count === 1 ? 'member' : 'members'}`}
    </button>
  );
}

// ---- Recursive tree node ----------------------------------------------------
function Node({ o, parent, selectedId, onShowSubordinates, onShowDetails }) {
  const leafChildren = o.leafChildren || [];
  const hasLeaves = leafChildren.length > 0;
  const [showLeaves, setShowLeaves] = useState(false);

  const T = parent
    ? TreeNode
    : (props) => (
      // eslint-disable-next-line react/jsx-props-no-spreading
      <Tree {...props} lineWidth="1.5px" lineColor="#d4d9e3" lineBorderRadius="10px">
        {props.children}
      </Tree>
    );

  const card = (node) => (
    <MemberNode
      org={node}
      selected={selectedId === node.id}
      onShowDetails={onShowDetails}
      onShowSubordinates={onShowSubordinates}
    />
  );

  return (
    <T label={card(o)}>
      {_.map(o.children, (c) => (
        <Node
          key={c.id}
          o={c}
          parent={o}
          selectedId={selectedId}
          onShowSubordinates={onShowSubordinates}
          onShowDetails={onShowDetails}
        />
      ))}
      {hasLeaves && (showLeaves ? (
        <TreeNode label={<LeafToggle count={leafChildren.length} expanded onClick={() => setShowLeaves(false)} />}>
          {_.map(leafChildren, (leaf) => <TreeNode key={leaf.id} label={card(leaf)} />)}
        </TreeNode>
      ) : (
        <TreeNode label={<LeafToggle count={leafChildren.length} expanded={false} onClick={() => setShowLeaves(true)} />} />
      ))}
    </T>
  );
}

// ---- Details panel ----------------------------------------------------------
function InfoRow({ label, value }) {
  return (
    <div className="flex items-start gap-3 py-1.5">
      <span className="text-sm text-ink-500 flex-shrink-0" style={{ width: 96 }}>{label}</span>
      <span className="text-sm font-medium text-ink-900 break-words min-w-0">{value || '—'}</span>
    </div>
  );
}

function TimeStat({ label, value, color }) {
  return (
    <div className="rounded-[10px] p-3" style={{ background: '#f7f8fa' }}>
      <span className="block text-[10px] font-semibold uppercase tracking-wider text-ink-500">{label}</span>
      <span className="block text-base font-bold tabular-nums mt-0.5" style={{ color: color || '#0e1e40' }}>{value}</span>
    </div>
  );
}

function DetailsPanel({ details, alerts, alertTypes, team, onClose }) {
  const worked = details.hoursWorked || 0;
  const idle = details.idleTime || 0;
  const expected = details.expectedHours || 0;
  // Quality (productive) hours = active time = worked minus idle.
  const quality = details.activeSeconds ?? Math.max(0, worked - idle);
  const engagement = engagementScore(details.activeSeconds || 0, details.totalSeconds || 0);
  const engColor = engagementColor(engagement);

  return (
    <div className="org-details">
      <div className="p-5">
        {/* Header */}
        <div className="flex items-start gap-3 mb-4">
          <NodeAvatar src={details.profilePicture} name={details.name} id={details.id} size={56} />
          <div className="min-w-0 flex-1">
            <div className="text-lg font-semibold text-ink-900 leading-tight truncate">{details.name}</div>
            <div className="text-sm text-ink-500 truncate">{details.role}</div>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="inline-flex items-center justify-center w-8 h-8 rounded-full border border-rule bg-white text-ink-500 hover:text-ink-900 hover:bg-gray-50 transition flex-shrink-0"
            style={{ borderStyle: 'solid' }}
          >
            <X size={16} />
          </button>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2 mb-5">
          <Link to="/members/alerts" className="flex-1">
            <button type="button" className="w-full inline-flex items-center justify-center gap-1.5 px-3 py-2 border border-rule rounded-[8px] text-sm font-medium text-ink-900 bg-white hover:bg-gray-50 transition" style={{ borderStyle: 'solid' }}>
              <Bell size={15} />
              View Alerts
            </button>
          </Link>
          <Link to="/members/team-engagement" className="flex-1">
            <button type="button" className="w-full inline-flex items-center justify-center gap-1.5 px-3 py-2 border border-rule rounded-[8px] text-sm font-medium text-ink-900 bg-white hover:bg-gray-50 transition" style={{ borderStyle: 'solid' }}>
              <Activity size={15} />
              Engagement
            </button>
          </Link>
        </div>

        {/* Time stats */}
        <div className="grid grid-cols-2 gap-2 mb-3">
          <TimeStat label="Hours Worked" value={convertSecToTimeFormat(worked, 'HH:MM')} color="#53A551" />
          <TimeStat label="Quality Hours" value={convertSecToTimeFormat(quality, 'HH:MM')} color="#3d5a8a" />
          <TimeStat label="Idle Time" value={convertSecToTimeFormat(idle, 'HH:MM')} color="#b9791f" />
          <TimeStat label="Expected Hours" value={convertSecToTimeFormat(expected, 'HH:MM')} color="#0e1e40" />
        </div>
        <div className="org-util-row">
          <span className="org-util-label">Engagement Score</span>
          <span className="org-util-pct" style={{ color: engColor }}>{`${engagement}%`}</span>
        </div>
        <div className="org-util-track mb-5">
          <div className="org-util-fill" style={{ width: `${engagement}%`, background: engColor }} />
        </div>

        <hr className="org-divider" />

        {/* Info */}
        <div className="mb-1">
          <InfoRow label="Designation" value={details.role} />
          <InfoRow label="Department" value={details.department} />
          <InfoRow label="Team" value={team} />
          <InfoRow label="Manager" value={details.manager} />
          <InfoRow label="Country" value={details.country} />
          <InfoRow label="Start Date" value={details.startDate} />
          <InfoRow label="Work Phone" value={details.workPhone} />
          <InfoRow label="Work Email" value={details.workEmail} />
        </div>

        {/* Alerts */}
        {alerts && alerts.length > 0 && (
          <>
            <hr className="org-divider" />
            <span className="block text-[11px] font-semibold uppercase tracking-wider text-ink-500 mb-3" style={{ letterSpacing: '0.05em' }}>
              Alerts
            </span>
            <div className="flex flex-col gap-4">
              {alerts.map((alert, index) => (
                // eslint-disable-next-line react/no-array-index-key
                <div key={index} className="flex flex-col gap-2">
                  <div className="flex items-start gap-2">
                    <div className="flex items-center justify-center flex-shrink-0" style={{ width: 32, height: 32, borderRadius: 16, background: '#FEFBE7' }}>
                      <img src={alertImg} alt="" style={{ width: 18, height: 18 }} />
                    </div>
                    <div className="min-w-0">
                      <div className="text-xs text-ink-500">Alert Triggered</div>
                      <div className="text-sm font-medium text-ink-900">{alertTypes[alert.alertTypeId - 1]?.triggerCondition || '—'}</div>
                    </div>
                  </div>
                  <div className="flex items-start gap-2">
                    <div className="flex items-center justify-center flex-shrink-0" style={{ width: 32, height: 32, borderRadius: 16, background: '#FCECE7' }}>
                      <img src={noticeImg} alt="" style={{ width: 18, height: 18 }} />
                    </div>
                    <div className="min-w-0">
                      <div className="text-xs text-ink-500">{alert.status === 'Pending' ? 'Action Taken' : 'Automated Action'}</div>
                      <div className="text-sm font-medium text-ink-900">{alert.status === 'Pending' ? 'Acknowledged Alert' : 'Requested Acknowledgement'}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ---- Zoom controls ----------------------------------------------------------
function ZoomControls({ transformRef }) {
  const call = (fn) => () => { const api = transformRef.current; if (api && api[fn]) api[fn](); };
  return (
    <div className="org-zoom">
      <button type="button" aria-label="Zoom in" onClick={call('zoomIn')}><Plus size={16} /></button>
      <button type="button" aria-label="Zoom out" onClick={call('zoomOut')}><Minus size={16} /></button>
      <button type="button" aria-label="Reset view" onClick={() => { const api = transformRef.current; if (api) api.resetTransform(); }}><Maximize2 size={16} /></button>
    </div>
  );
}

function OrganizationChart() {
  const { activeWorkspace } = useContext(UserContext);
  const [organization, setOrganization] = useState({});
  const [date, setDate] = useState(currentMonthWeekRange());
  const [managers, setManagers] = useState([]);
  const [manager, setManager] = useState('');
  const [showSubordinates, setShowSubordinates] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [subordinates, setSubordinates] = useState([]);
  const [details, setDetails] = useState({});
  const [expectedHours, setExpectedHours] = useState(0);
  const [teams, setTeams] = useState([]);
  const [teammates, setTeammates] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [alertTypes, setAlertTypes] = useState([]);

  const transformRef = useRef(null);
  const canvasRef = useRef(null);

  const buildHierarchy = (members, parentId, dayDifference, mgrs = new Set()) => {
    const directSubordinates = members.filter((m) => m.reportingManager === parentId);

    const children = directSubordinates.filter((m) => {
      const isManager = members.some((sub) => sub.reportingManager === m.id);
      if (isManager) {
        mgrs.add({
          id: m.id,
          value: m.id,
          text: `${m.user?.firstName || 'Unknown'} ${m.user?.lastName || ''}`,
        });
      }
      return isManager;
    });

    let totalSeconds = 0;
    let activeSeconds = 0;
    let expected = 0;

    const subOrg = children.map((child) => {
      const childHierarchy = buildHierarchy(members, child.id, dayDifference, mgrs);
      totalSeconds += childHierarchy.hierarchy.totalSeconds;
      activeSeconds += childHierarchy.hierarchy.activeSeconds;
      expected += childHierarchy.hierarchy.expectedHours;
      return childHierarchy.hierarchy;
    });

    const leafChildren = directSubordinates
      .filter((m) => !members.some((sub) => sub.reportingManager === m.id))
      .map((leaf) => buildHierarchy(members, leaf.id, dayDifference, mgrs).hierarchy);

    const allSubordinates = directSubordinates.flatMap((sub) => {
      const childHierarchy = buildHierarchy(members, sub.id, dayDifference, mgrs);
      return [sub, ...childHierarchy.hierarchy.subordinates];
    });

    const member = members.find((m) => m.id === parentId) || {};
    const memberTotalSeconds = member.dailyActivity ? +member.dailyActivity.totalSeconds || 0 : 0;
    const memberActiveSeconds = member.dailyActivity ? +member.dailyActivity.activeSeconds || 0 : 0;
    const memberExpectedHours = 3600 * 8 * dayDifference;

    totalSeconds += memberTotalSeconds;
    activeSeconds += memberActiveSeconds;
    expected += memberExpectedHours;

    let mgr;
    mgrs.forEach((m) => { if (m.id === member.reportingManager) mgr = m.text; });

    return {
      hierarchy: {
        id: parentId,
        name: `${member.user?.firstName || 'Unknown'} ${member.user?.lastName || ''}`,
        role: member.accountType || 'Unknown',
        department: member.department,
        hoursWorked: totalSeconds,
        idleTime: totalSeconds - activeSeconds,
        expectedHours: expected,
        totalSeconds,
        activeSeconds,
        collapsed: false,
        profilePicture: member.user?.profilePicture || null,
        children: subOrg,
        leafChildren,
        subordinates: allSubordinates,
        workEmail: member.user?.email,
        workPhone: member.user?.phone || member.user?.workPhone || member.user?.phoneNumber || null,
        manager: mgr,
        startDate: member.firstActivityTime,
        country: member.user?.userLocation?.country,
      },
      managers: Array.from(mgrs),
    };
  };

  const getOrgChartData = async (memberId = null) => {
    const [startDate, endDate] = date;
    const dates = getDatesInRange(startDate, endDate);
    const startOfDay = moment(dates[0]).utc().startOf('day').format('YYYY-MM-DD HH:mm:ss');
    const endOfDay = moment(dates.at(-1)).utc().endOf('day').format('YYYY-MM-DD HH:mm:ss');
    const response = await sendRequest({
      method: 'get',
      url: API.WORKSPACE.organizationChart(activeWorkspace.id),
      params: { startOfDay, endOfDay, memberId },
    });

    const { members, rootMember } = response.data;

    const calculateWeekdays = (start, end) => {
      let weekdays = 0;
      const current = moment(start).startOf('day');
      const endMoment = moment(end).startOf('day');
      while (current.isSameOrBefore(endMoment)) {
        const day = current.isoWeekday();
        // eslint-disable-next-line no-plusplus
        if (day !== 6 && day !== 7) weekdays++;
        current.add(1, 'day');
      }
      return weekdays;
    };

    const dayDifference = calculateWeekdays(startOfDay, endOfDay);
    const { hierarchy, managers: mgrs } = buildHierarchy(members, rootMember.id, dayDifference);
    setOrganization(hierarchy);
    setExpectedHours(dayDifference * 8 * 3600);
    if (!memberId) setManagers(mgrs);
  };

  useEffect(() => {
    getOrgChartData(manager || null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [date]);

  // Figma-style canvas navigation: Cmd/Ctrl + wheel = cursor-anchored zoom;
  // plain wheel / trackpad two-finger scroll = pan. (ctrlKey also covers
  // browser trackpad pinch on macOS; metaKey covers Cmd, ctrlKey covers Windows.)
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;
    const MIN = 0.3;
    const MAX = 2.5;
    const handleWheel = (e) => {
      const api = transformRef.current;
      if (!api?.instance?.transformState) return;
      e.preventDefault();
      const { scale, positionX, positionY } = api.instance.transformState;
      if (e.metaKey || e.ctrlKey) {
        const rect = canvas.getBoundingClientRect();
        const ox = e.clientX - rect.left;
        const oy = e.clientY - rect.top;
        const factor = Math.exp(-e.deltaY * 0.0018);
        const newScale = Math.min(MAX, Math.max(MIN, scale * factor));
        if (newScale === scale) return;
        const ratio = newScale / scale;
        const nx = ox - (ox - positionX) * ratio;
        const ny = oy - (oy - positionY) * ratio;
        api.setTransform(nx, ny, newScale, 0);
      } else {
        api.setTransform(positionX - e.deltaX, positionY - e.deltaY, scale, 0);
      }
    };
    canvas.addEventListener('wheel', handleWheel, { passive: false });
    return () => canvas.removeEventListener('wheel', handleWheel);
  }, []);

  const handleManagerChange = (e) => {
    const { value } = e.target;
    setManager(value);
    getOrgChartData(value || null);
  };

  const handleShowSubordinates = (org) => {
    setShowSubordinates(true);
    setSubordinates(org);
  };

  const getAlerts = async (memberId) => {
    const [startDate, endDate] = date;
    const dates = getDatesInRange(startDate, endDate);
    const startOfDay = moment(dates[0]).utc().startOf('day').format('YYYY-MM-DD HH:mm:ss');
    const endOfDay = moment(dates.at(-1)).utc().endOf('day').format('YYYY-MM-DD HH:mm:ss');
    const response = await sendRequest({
      method: 'get',
      url: API.WORKSPACE.getAlertOfMember(activeWorkspace.id),
      params: { startOfDay, endOfDay, memberId },
    });
    if (response.success) setAlerts(response.data.alerts);
  };

  const handleShowDetails = (org) => {
    setShowDetails(true);
    setDetails(org);
    getAlerts(org.id);
  };

  const getTeams = async () => {
    const response = await sendRequest({ method: 'get', url: API.WORKSPACE.getAllTeamInfo(activeWorkspace.id), timeout: 60000 });
    if (response.success) setTeams(response.data.teams);
  };

  const getTeammates = async () => {
    const response = await sendRequest({ method: 'get', url: API.WORKSPACE.getAllTeamMates(), timeout: 60000 });
    if (response.success) setTeammates(response.data.teammates);
  };

  const getAlertTypes = async () => {
    const response = await sendRequest({ method: 'get', url: API.WORKSPACE.getAlertTypes(), timeout: 60000 });
    if (response.success) setAlertTypes(response.data.alertTypes);
  };

  useEffect(() => {
    getTeams();
    getTeammates();
    getAlertTypes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getCurrentTeam = (id) => {
    const names = [];
    teammates.forEach((mate) => {
      if (mate.memberId === id) {
        teams.forEach((t) => { if (t.id === mate.teamId) names.push(t.name); });
      }
    });
    return names.join(', ');
  };

  return (
    <div style={{ height: '100vh', display: 'flex', flexDirection: 'column' }}>
      <Navbar />
      <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
        <Sidebar />

        {showSubordinates && (
          <SubordinatesModal
            closeModal={() => setShowSubordinates(false)}
            subordinates={subordinates}
            expectedHours={expectedHours}
          />
        )}

        <div style={{ flex: 1, marginLeft: 240, marginTop: 60, display: 'flex', flexDirection: 'column', overflow: 'hidden', height: '100%', fontFamily: 'Inter, sans-serif' }}>
          {/* Header */}
          <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between bg-white border-b border-rule" style={{ borderBottomStyle: 'solid', padding: '18px 32px 18px 40px', flexShrink: 0 }}>
            <div className="min-w-0">
              <h1 className="font-bold text-ink-900 leading-tight text-2xl lg:text-[28px] lg:leading-9" data-testid="title" data-tour="org-chart">
                Organization Chart
              </h1>
              <p className="text-sm text-ink-500 mt-0.5">
                Visualize your reporting structure and team activity
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-2 flex-shrink-0">
              <select
                value={manager}
                onChange={handleManagerChange}
                className={`${STYLED_SELECT_CLASS} min-w-[200px]`}
                style={STYLED_SELECT_STYLE}
              >
                <option value="">All Managers</option>
                {managers.map((m) => <option key={m.value} value={m.value}>{m.text}</option>)}
              </select>
              <div className="flex-shrink-0" style={{ width: 240 }}>
                <DateRangePicker value={date} onChange={(d) => setDate(d)} className="w-full" placement="bottomEnd" />
              </div>
            </div>
          </div>

          {/* Chart + details */}
          <div style={{ display: 'flex', flexDirection: 'row', flex: 1, minHeight: 0 }}>
            <div ref={canvasRef} className="org-canvas">
              <TransformWrapper
                ref={transformRef}
                initialScale={0.8}
                minScale={0.3}
                maxScale={2.5}
                wheel={{ disabled: true }}
                pinch={{ disabled: false }}
                doubleClick={{ disabled: true }}
                panning={{ velocityDisabled: true }}
                limitToBounds={false}
                centerOnInit
              >
                <TransformComponent
                  wrapperStyle={{ width: '100%', height: '100%', cursor: 'grab' }}
                  contentStyle={{ width: '100%', height: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center', padding: 48 }}
                >
                  {organization && organization.id ? (
                    <Node
                      o={organization}
                      selectedId={showDetails ? details.id : null}
                      onShowSubordinates={handleShowSubordinates}
                      onShowDetails={handleShowDetails}
                    />
                  ) : <span />}
                </TransformComponent>
              </TransformWrapper>

              <ZoomControls transformRef={transformRef} />

              {/* hint */}
              <div
                className="absolute text-xs text-ink-500 bg-white border border-rule rounded-[8px] px-3 py-1.5 flex items-center gap-1.5"
                style={{ left: 20, bottom: 20, borderStyle: 'solid', boxShadow: '0 2px 8px rgba(14,30,64,0.08)' }}
              >
                <Users size={13} />
                Scroll to pan · ⌘/Ctrl + scroll to zoom · drag to move
              </div>
            </div>

            {showDetails && (
              <DetailsPanel
                details={details}
                alerts={alerts}
                alertTypes={alertTypes}
                team={getCurrentTeam(details.id)}
                onClose={() => setShowDetails(false)}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default OrganizationChart;
